'use strict';

/**
 * Single-project **PipelineStatus** data path: loads append-only facts, maps to graph node view-models (`PipelineStatusStage[]`),
 * No Redux pipeline slice, no legacy progress UI, no SignalR payload in state (hub only invalidates RTK tags). Pipeline status is informational only and never gates the Publish action.
 *
 * Graph input: live `stages` come from PortalApi `snapshot.graph` composed with local presentation (`composePipelineStatusLiveGraph`).
 * Facts bind by API `factMatch`: `workflow` + `pipeline` + `stage` + `service` (see `pipelineStatusAdapter`).
 * (`comparePipelineFactsRecency` in `pipelineStatusAdapter` is deterministic).
 *
 * ## PortalApi `GET …/admin/pipeline-status/projects/{id}/facts`
 *
 * Response body: `{ facts, iterationManifests, graph }` (camelCase). A payload without `graph` fails visibly.
 *
 * **`ENABLE_PIPELINE_STATUS`** (see `@/config/env.config`) must be on for this hook to query PortalApi. When it is off, the hook
 * skips RTK and returns empty stages (panel is not mounted from `Project.jsx`).
 *
 * When **`USE_PIPELINE_STATUS_API_MOCKS`** is **`true`** (Jest / rare local override; keep false
 * in alpha and in local `.env`), the Portal RTK `baseQuery` returns fixture JSON for
 * admin/pipeline-status/* GETs and **no network request** is made. Otherwise responses come from
 * the live Portal admin pipeline-status routes. The User-menu mock showcase does not use this flag.
 *
 * With mocks on, **`PIPELINE_STATUS_MOCK_SCENARIO`** may override the flow key; when unset, the scenario follows `ProjectDescription.pipeline` like live mode (see `requirePipelineStatusMockScenarioFromEnv`). Use {@link useResolvedPipelineStatusFlowScenario} so the graph matches the fixture rows. With mocks off, pass the key from {@link pipelineStatusFlowScenarioFromProjectPipeline} using `ProjectDescription.pipeline`.
 *
 * Fact rows use backend `status` values `Started` | `Completed` | `Failed` (see `pipelineStatusAdapter.js`).
 *
 * Requires **`canViewPipelineStatus`** (dedicated pipeline-status read permission; PortalApi project status routes). Callers without that permission should not mount the panel; the hook skips RTK when permission is absent.
 *
 * **Current-flow selection:** the backend snapshot's additive `currentFlows` projection scopes
 * V7 facts/manifests to the current publishing run; when absent (older PortalApi) the same
 * selection is computed client-side by {@link selectCurrentV7Run}. Old correlations never mix
 * into the current tracker.
 *
 * **Polling fallback:** SignalR invalidation remains the refresh hint; the facts query also
 * polls at `PIPELINE_STATUS_POLL_INTERVAL_MS` (0 disables) so disconnects self-heal. RTK Query
 * stops polling automatically when the last subscriber unmounts.
 */

import { useMemo } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import {
	ENABLE_PIPELINE_STATUS,
	PIPELINE_STATUS_POLL_INTERVAL_MS,
	USE_PIPELINE_STATUS_API_MOCKS,
} from '@/config/env.config';
import { usePipelineStatusFactsQuery } from '@/redux/services/portal/projects.api';
import { usePermissions } from '@/hooks/permissions.hook';
import { CAN_VIEW_PIPELINE_STATUS } from '@/security/permissionTypes';
import { composePipelineStatusLiveGraph } from './composePipelineStatusLiveGraph';
import {
	pipelineStatusFlowScenarioFromProjectPipeline,
	requirePipelineStatusFlowScenarioKey,
} from './pipelineStatusFlowScenarios';
import {
	flowScenarioForMockScenario,
	requirePipelineStatusMockScenarioFromEnv,
} from './mocks/pipelineStatusFacts.mock';
import { buildPipelineStatusGraphViewModel } from './pipelineStatusAdapter';

/**
 * Resolves which pipeline-status graph scenario to use: with API mocks, the env scenario (must match mock facts); otherwise `ProjectDescription.pipeline`.
 *
 * @param {unknown} projectPipeline Portal `ProjectDescription.pipeline` when mocks are off.
 * @returns {string}
 */
export function useResolvedPipelineStatusFlowScenario(projectPipeline) {
	return useMemo(() => {
		if (USE_PIPELINE_STATUS_API_MOCKS) {
			// Mock scenario keys are a superset of flow keys; the graph needs the flow key.
			return flowScenarioForMockScenario(requirePipelineStatusMockScenarioFromEnv(projectPipeline));
		}
		return pipelineStatusFlowScenarioFromProjectPipeline(projectPipeline);
	}, [projectPipeline]);
}

/**
 * Normalizes RTK / JSON rows into `PipelineStatusFact[]` for the adapter.
 * @param {unknown[] | undefined} rows
 * @returns {import('./pipelineStatusTypes').PipelineStatusFact[]}
 */
export function mapApiRowsToPipelineStatusFacts(rows) {
	if (!Array.isArray(rows)) return [];
	return rows.map((r) => {
		const row = /** @type {Record<string, unknown>} */ (r);
		return {
			id: row.id != null ? String(row.id) : '',
			scope: row.scope != null ? String(row.scope) : '',
			workflow: row.workflow != null ? String(row.workflow) : '',
			subjectId: row.subjectId != null ? String(row.subjectId) : '',
			pipeline: row.pipeline != null ? String(row.pipeline) : '',
			stage: row.stage != null ? String(row.stage) : '',
			status: row.status != null ? String(row.status) : '',
			occurredAt: row.occurredAt != null ? String(row.occurredAt) : undefined,
			correlationId: row.correlationId != null ? String(row.correlationId) : null,
			service: row.service != null ? String(row.service) : undefined,
			serviceVersion: row.serviceVersion != null ? String(row.serviceVersion) : undefined,
			iterationDimension:
				row.iterationDimension != null && String(row.iterationDimension).trim() !== ''
					? String(row.iterationDimension)
					: null,
			iterationValue:
				row.iterationValue != null && String(row.iterationValue).trim() !== ''
					? String(row.iterationValue)
					: null,
			detailsJson:
				row.detailsJson != null && String(row.detailsJson).trim() !== '' ? String(row.detailsJson) : null,
		};
	});
}

/**
 * @param {unknown[] | undefined} rows
 * @returns {import('./pipelineStatusTypes').PipelineStatusIterationManifest[]}
 */
export function mapApiRowsToPipelineStatusIterationManifests(rows) {
	if (!Array.isArray(rows)) return [];
	return rows.map((r) => {
		const row = /** @type {Record<string, unknown>} */ (r);
		const rawVals = row.iterationValues ?? row.IterationValues;
		const iterationValues = Array.isArray(rawVals) ? rawVals.map((v) => String(v)) : [];
		return {
			id: row.id != null ? String(row.id) : '',
			correlationId: row.correlationId != null ? String(row.correlationId) : '',
			scope: row.scope != null ? String(row.scope) : '',
			workflow: row.workflow != null ? String(row.workflow) : '',
			pipeline: row.pipeline != null ? String(row.pipeline) : '',
			parentStage: row.parentStage != null ? String(row.parentStage) : '',
			parentService:
				row.parentService != null && String(row.parentService).trim() !== ''
					? String(row.parentService)
					: null,
			subjectId: row.subjectId != null ? String(row.subjectId) : '',
			iterationDimension: row.iterationDimension != null ? String(row.iterationDimension) : '',
			iterationValues,
			createdAtUtc: row.createdAtUtc != null ? String(row.createdAtUtc) : '',
			sourceService: row.sourceService != null ? String(row.sourceService) : '',
		};
	});
}

/**
 * Normalizes `GET …/facts` JSON into facts + iteration manifests (supports legacy array-only payloads).
 * @param {unknown} data
 * @returns {{
 *   facts: import('./pipelineStatusTypes').PipelineStatusFact[],
 *   iterationManifests: import('./pipelineStatusTypes').PipelineStatusIterationManifest[],
 *   graph: unknown,
 * }}
 */
export function mapApiPipelineStatusSnapshotPayload(data) {
	if (data == null) return { facts: [], iterationManifests: [], currentFlows: null, graph: null };
	if (Array.isArray(data)) {
		return { facts: mapApiRowsToPipelineStatusFacts(data), iterationManifests: [], currentFlows: null, graph: null };
	}
	const o = /** @type {Record<string, unknown>} */ (data);
	const factsRaw = o.facts ?? o.Facts;
	const manRaw = o.iterationManifests ?? o.IterationManifests;
	return {
		facts: mapApiRowsToPipelineStatusFacts(Array.isArray(factsRaw) ? /** @type {unknown[]} */ (factsRaw) : []),
		iterationManifests: mapApiRowsToPipelineStatusIterationManifests(
			Array.isArray(manRaw) ? /** @type {unknown[]} */ (manRaw) : [],
		),
		currentFlows: mapApiCurrentFlows(o.currentFlows ?? o.CurrentFlows),
		graph: o.graph ?? o.Graph ?? null,
	};
}

/**
 * Maps PortalApi current publication correlations. Null when the payload has no projection.
 * Ready and Failed are not on this object.
 *
 * @param {unknown} raw
 * @returns {{
 *   publications: Array<{
 *     pipeline: string,
 *     subjectId: string,
 *     correlationId: string,
 *     startedAt: string | undefined,
 *     supersedesEarlier: boolean,
 *   }>,
 * } | null}
 */
export function mapApiCurrentFlows(raw) {
	if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) return null;
	const o = /** @type {Record<string, unknown>} */ (raw);
	const publicationsRaw = o.publications ?? o.Publications;

	/** @param {unknown} v */
	const str = (v) => (v == null ? '' : String(v));
	/** @param {unknown} v */
	const strOrUndef = (v) => (v == null || String(v).trim() === '' ? undefined : String(v));

	const publications = Array.isArray(publicationsRaw)
		? publicationsRaw
			.filter((p) => p != null && typeof p === 'object')
			.map((p) => {
				const row = /** @type {Record<string, unknown>} */ (p);
				return {
					pipeline: str(row.pipeline ?? row.Pipeline),
					subjectId: str(row.subjectId ?? row.SubjectId),
					correlationId: str(row.correlationId ?? row.CorrelationId),
					startedAt: strOrUndef(row.startedAt ?? row.StartedAt),
					supersedesEarlier: Boolean(row.supersedesEarlier ?? row.SupersedesEarlier),
				};
			})
		: [];

	return { publications };
}

/** @param {string | null | undefined} correlationId */
export function isEmptyPipelineCorrelation(correlationId) {
	const c = correlationId == null ? '' : String(correlationId).trim();
	return c === '' || c === '00000000-0000-0000-0000-000000000000';
}

/**
 * Keeps facts and manifests for one pipeline's current publication correlation.
 * When that publication is absent, that pipeline's facts are dropped so an older run cannot become Ready.
 * Facts for other pipelines pass through untouched.
 *
 * @param {import('./pipelineStatusTypes').PipelineStatusFact[]} facts
 * @param {import('./pipelineStatusTypes').PipelineStatusIterationManifest[]} iterationManifests
 * @param {ReturnType<typeof mapApiCurrentFlows>} currentFlows
 * @param {string} [pipelineName] Pipeline whose publication scopes the facts. Defaults to V7.
 * @returns {{
 *   facts: import('./pipelineStatusTypes').PipelineStatusFact[],
 *   iterationManifests: import('./pipelineStatusTypes').PipelineStatusIterationManifest[],
 *   currentV7CorrelationId: string | null,
 * }}
 */
export function selectCurrentV7Run(facts, iterationManifests, currentFlows, pipelineName = 'v7') {
	const want = String(pipelineName ?? 'v7').trim().toLowerCase();
	const isPipeline = (p) => String(p ?? '').trim().toLowerCase() === want;
	const publication = (currentFlows?.publications ?? []).find(
		(p) => String(p.pipeline ?? '').trim().toLowerCase() === want
			&& !isEmptyPipelineCorrelation(p.correlationId),
	);
	const correlation = publication ? publication.correlationId.trim() : null;

	if (!correlation) {
		return {
			facts: facts.filter((f) => !isPipeline(f.pipeline)),
			iterationManifests: iterationManifests.filter((m) => !isPipeline(m.pipeline)),
			currentV7CorrelationId: null,
		};
	}

	return {
		facts: facts.filter(
			(f) => !isPipeline(f.pipeline) || String(f.correlationId ?? '').trim() === correlation,
		),
		iterationManifests: iterationManifests.filter(
			(m) => !isPipeline(m.pipeline) || String(m.correlationId ?? '').trim() === correlation,
		),
		currentV7CorrelationId: correlation,
	};
}

/** Dedicated pipeline-status read permission; mock mode bypasses the live login check. */
export function useCanAccessProjectPipelineStatus() {
	const { checkUserHasPermissions } = usePermissions();
	// Mock mode is a local/dev-only configuration (all deployed envs ship mocks off);
	// bypassing the permission there lets the mock preview run without a live login.
	if (USE_PIPELINE_STATUS_API_MOCKS) return true;
	return checkUserHasPermissions(CAN_VIEW_PIPELINE_STATUS);
}

/**
 * @param {string | undefined} projectId
 * @param {unknown} flowScenarioKey Known flow scenario (e.g. `singleV7ProjectFlow` from `pipelineStatusFlowScenarios`).
 * @param {unknown} [projectPipeline] Portal `ProjectDescription.pipeline`. Forwarded on the facts
 *   query so mock mode serves the matching fixture (the project page does not populate RTK `projectId`).
 * @returns {{
 *   definition: import('./pipelineStatusTypes').PipelineStatusGraphDefinition,
 *   facts: import('./pipelineStatusTypes').PipelineStatusFact[],
 *   iterationManifests: import('./pipelineStatusTypes').PipelineStatusIterationManifest[],
 *   stages: import('./pipelineStatusTypes').PipelineStatusStage[],
 *   currentFlows: ReturnType<typeof mapApiCurrentFlows>,
 *   currentV7CorrelationId: string | null,
 *   isLoading: boolean,
 *   isFetching: boolean,
 *   error: unknown,
 *   refetch: () => Promise<unknown>,
 * }}
 */
export function usePipelineStatus(projectId, flowScenarioKey, projectPipeline) {
	const flowKey = requirePipelineStatusFlowScenarioKey(flowScenarioKey);
	const trackerEnabled = ENABLE_PIPELINE_STATUS;
	const canAccessProjectPipelineStatus = useCanAccessProjectPipelineStatus();

	const queryArg = useMemo(() => {
		if (!trackerEnabled || !canAccessProjectPipelineStatus) return skipToken;
		if (!projectId) return skipToken;
		return { projectId, pipeline: projectPipeline };
	}, [trackerEnabled, canAccessProjectPipelineStatus, projectId, projectPipeline]);

	// Polling fallback in addition to SignalR tag invalidation; RTK Query prevents
	// duplicate polling loops per cache entry and stops when subscribers unmount.
	const query = usePipelineStatusFactsQuery(queryArg, {
		pollingInterval: PIPELINE_STATUS_POLL_INTERVAL_MS > 0 ? PIPELINE_STATUS_POLL_INTERVAL_MS : undefined,
		skipPollingIfUnfocused: true,
	});

	const { facts: allFacts, iterationManifests: allManifests, currentFlows, graph: apiGraph } = useMemo(
		() => mapApiPipelineStatusSnapshotPayload(query.data),
		[query.data],
	);

	// Current-run scoping (V7): superseded runs and legacy empty correlations must not
	// dress up the current tracker. Last known data stays rendered during refetches.
	const { facts, iterationManifests, currentV7CorrelationId } = useMemo(
		() => selectCurrentV7Run(allFacts, allManifests, currentFlows),
		[allFacts, allManifests, currentFlows],
	);

	const { definition, graphError } = useMemo(() => {
		if (query.data == null) return { definition: null, graphError: null };
		try {
			return { definition: composePipelineStatusLiveGraph(apiGraph), graphError: null };
		} catch (err) {
			return { definition: null, graphError: err };
		}
	}, [query.data, apiGraph]);

	const stages = useMemo(
		() => (definition
			? buildPipelineStatusGraphViewModel(definition, facts, iterationManifests, projectId)
			: []),
		[definition, facts, iterationManifests, projectId],
	);

	return {
		definition,
		facts,
		iterationManifests,
		currentFlows,
		currentV7CorrelationId,
		stages,
		isLoading: !trackerEnabled || !canAccessProjectPipelineStatus ? false : query.isLoading,
		isFetching: !trackerEnabled || !canAccessProjectPipelineStatus ? false : query.isFetching,
		error: !trackerEnabled || !canAccessProjectPipelineStatus ? undefined : (graphError ?? query.error),
		refetch: query.refetch,
	};
}
